type AdminLocale = typeof import('../en/admin').default

const admin = {
	title: 'Administración',
	subtitle: 'Gestionar datos del sistema',
	configStudio: {
		title: 'Config Studio',
		description:
			'Edita la configuración activa por namespaces. Usa editores rápidos para toggles y controles guiados para el trabajo operativo.',
		environment: 'Contexto',
		actions: {
			export: 'Exportar',
			saveDraft: 'Guardar cambios',
			promote: 'Registrar snapshot',
			showDiff: 'Mostrar diff',
			hideDiff: 'Ocultar diff',
			resetFromProduction: 'Restablecer desde el último valor guardado',
		},

		preview: 'Vista previa (solo lectura) del valor actual:',
		editor: {
			title: 'Editor',
		},
		oneliners: {
			toggle: 'Activa o desactiva este ajuste.',
			select: 'Selecciona un valor.',
			multiselect: 'Selecciona uno o varios valores.',
			text: 'Introduce un valor de texto.',
			number: 'Introduce un valor numérico.',
			json: 'Edita JSON. Debe ser válido para aplicarse.',
		},
		diff: {
			staging: 'Borrador local',
			production: 'Valor guardado',
			added: 'Añadidos',
			removed: 'Eliminados',
			changed: 'Cambiados',
		},
		navigationRoutes: {
			area: 'Área',
			routeKey: 'Clave de ruta',
			href: 'Href',
			showCreateButton: 'Mostrar botón crear',
			requiresAuth: 'Requiere autenticación',
			allowedRoles: 'Roles permitidos',
		},
		i18nOverrides: {
			search: 'Buscar claves',
			newKey: 'Nueva clave',
			add: 'Añadir',
			noResults:
				'No hay claves para mostrar. Usa el formulario de nueva clave para añadir un override.',
			namespace: 'Namespace',
			group: 'Grupo',
			overriddenOnly: 'Solo sobrescritas',
			baseValue: 'Base (EN)',
			overrideEN: 'Override (EN)',
			overrideES: 'Override (ES)',
			fillFromBase: 'Rellenar desde base',
		},
		logging: {
			level: 'Nivel de logs',
			categories: {
				router: 'Router',
				handler: 'Handler',
				billing: 'Billing',
				webhook: 'Webhook',
				conditions: 'Condiciones',
				loaders: 'Loaders',
			},
			levels: {
				trace: 'trace',
				debug: 'debug',
				info: 'info',
				warn: 'warn',
				error: 'error',
			},
		},
		sections: {
			adminPreferences: {
				title: 'Preferencias de Admin',
				description:
					'Ajustes globales de la aplicación y toggles de funcionalidades.',
			},
			courseFeatures: {
				title:
					'Características {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
				description:
					'Toggles de funcionalidades para la experiencia y capacidades {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
			},
			navigationAdminRoutes: {
				title: 'Rutas de navegación de Admin',
				description:
					'Configura las rutas de navegación del área de administración.',
			},
			navigationDashboardRoutes: {
				title: 'Rutas de navegación del dashboard',
				description: 'Configura las rutas de navegación del dashboard.',
			},
			navigationRoutes: {
				title: 'Enlaces en menús',
				description: 'Gestiona las rutas de navegación por área y grupo.',
			},
			navigationPaths: {
				title: 'Rutas',
				description:
					'Edita las rutas y su gating (habilitado, requiere auth, roles).',
			},
			navigationSidebarUser: {
				title: 'Barra lateral del usuario',
				description:
					'Configura los elementos mostrados en la barra lateral del usuario.',
			},
			permissions: {
				title: 'Permisos',
				description: 'Permisos de rol y reglas de acceso.',
			},
			logging: {
				title: 'Logging',
				description: 'Configuración de logging de la aplicación.',
			},
			loggingAction: {
				title: 'Logging de acciones',
				description:
					'Opciones de logging específicas para acciones de usuario.',
			},
			i18nOverrides: {
				title: 'Overrides de traducción',
				description:
					'Gestiona overrides de traducción por idioma con flujo de staging/promoción.',
			},
			platformImport: {
				title: 'Importación',
			},
			marketingAcquisition: {
				title: 'Captación',
				description: 'Configura el lead magnet y la captación principal.',
			},
		},
		groups: {
			featureToggles: 'Toggles de funcionalidades',
			courseFeatureToggles:
				'Toggles de funcionalidades {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			settings: 'Ajustes',
			billing: {
				general: 'General',
				portal: 'Portal',
				churn: 'Abandono',
				leadMagnet: 'Lead magnet',
			},
			actionLogging: {
				general: 'General',
				development: 'Desarrollo',
				production: 'Producción',
				testing: 'Pruebas',
			},
			adminPreferences: {
				general: 'General',
				course: '{{mainEntityLabel}}',
				topic: 'Tema',
				resource: 'Recurso',
				collections: '{{collectionEntityLabelPlural}}',
				userSettings: 'Ajustes de usuario',
				sidebar: 'Barra lateral',
			},
			navigationPaths: {
				core: 'Rutas base',
				auth: 'Rutas de auth',
				courses: 'Rutas de {{mainEntityLabelPluralLower}}',
				marketing: 'Rutas de marketing',
				publicCourses:
					'Rutas de {{mainEntityLabelPluralLower}} públic{{mainEntityGenderSuffixPlural}}',
				admin: 'Rutas de admin',
				static: 'Rutas estáticas',
			},
			platformImport: {
				general: 'Ajustes generales',
				endpoints: 'Endpoints',
			},
			marketingAcquisition: {
				leadMagnet: 'Lead magnet',
			},
		},
	},
	quickSettings: {
		title: 'Configuración',
		description: 'Configura los ajustes y funciones de tu plataforma.',
		model: {
			description:
				'Los cambios que guardas aquí actualizan la configuración en vivo de la marca activa. Esta superficie no gestiona un entorno de staging separado.',
		},
		unsavedChanges: 'Cambios sin guardar',
		fieldCount: '{{count}} campos',
		criticalCount: '{{count}} críticos',
		warningTitle: 'Revísalo antes de cambiarlo',
		importance: {
			critical: 'Crítico',
			standard: 'Estándar',
			advanced: 'Avanzado',
		},
		entity: {
			course: '{{mainEntityLabelPlural}}',
			topic: 'Temas',
			resource: 'Recursos',
			user: 'Usuarios',
			platform: 'Plataforma',
		},
		roles: {
			visibleTo: 'Visible para {{roles}}',
		},
		overview: {
			title: 'Resumen de configuración',
			description:
				'Cada sección muestra su alcance, nivel de riesgo y guía operativa antes de guardar.',
			sectionSummary: '{{sectionCount}} secciones - {{fieldCount}} campos',
			changedCount: '{{count}} con cambios',
		},
		sections: {
			general: 'General',
			'general.description': 'Ajustes de plataforma, idioma y estudios.',
			billing: 'Facturación',
			'billing.description': 'Ajustes de suscripción y pagos.',
			leadMagnet: 'Lead magnet',
			'leadMagnet.description':
				'Configura la colección gratuita principal de captación.',
			courses: '{{mainEntityLabelPlural}}',
			'courses.description':
				'Funciones y opciones de gestión de {{mainEntityLabelPluralLower}}.',
			content: 'Contenido',
			'content.description': 'Ajustes de temas, recursos y contenido IA.',
			userSettings: 'Ajustes de usuario',
			'userSettings.description':
				'Opciones de cuenta y páginas de configuración.',
			navigation: 'Navegación',
			'navigation.description': 'Habilitar o deshabilitar rutas y páginas.',
		},
		groups: {
			platform: 'Plataforma',
			language: 'Idioma',
			brand: 'Marca',
			studios: 'Estudios',
			permissions: 'Permisos',
			subscription: 'Suscripción',
			churn: 'Prevención de abandono',
			portal: 'Portal de cliente',
			leadMagnet: 'Lead magnet',
			courseFeatures:
				'Funciones {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			'courseFeatures.description':
				'Activa funciones {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} como planes de estudio, diagramas y notas.',
			courseManagement: 'Gestión de {{mainEntityLabelPluralLower}}',
			'courseManagement.description':
				'Controla plantillas, instancias, duplicación y exportación.',
			courseVisibility: 'Visibilidad de {{mainEntityLabelPluralLower}}',
			'courseVisibility.description':
				'Configura opciones de marketing y vista previa pública.',
			courseArtifacts:
				'Artefactos {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			'courseArtifacts.description':
				'Configura artefactos predeterminados y auto-generación.',
			topics: 'Temas',
			'topics.description':
				'Configura contenido, recursos, notas y diagramas de temas.',
			aiFeatures: 'Funciones de IA',
			'aiFeatures.description':
				'Habilita o deshabilita generación de contenido con LLM.',
			collections: 'Colecciones',
			'collections.description':
				'Habilita {{collectionEntityLabelPluralLower}} y colecciones de {{mainEntityLabelPluralLower}}.',
			settingsPages: 'Páginas de configuración',
			'settingsPages.description':
				'Controla qué páginas de configuración están disponibles.',
			coreRoutes: 'Rutas principales',
			'coreRoutes.description':
				'Habilita o deshabilita rutas esenciales de la aplicación.',
			authRoutes: 'Autenticación',
			'authRoutes.description':
				'Controla rutas de login, registro y recuperación de contraseña.',
			courseRoutes: 'Rutas de {{mainEntityLabelPluralLower}}',
			'courseRoutes.description':
				'Habilita o deshabilita páginas relacionadas con {{mainEntityLabelPluralLower}}.',
			adminRoutes: 'Rutas de admin',
			'adminRoutes.description':
				'Controla la visibilidad de rutas del panel de administración.',
		},
	},
	common: {
		requiresAuth: 'Requiere autenticación',
		allowedRoles: 'Roles permitidos',
		deniedRoles: 'Roles denegados',
	},
	drawer: {
		createTitle: 'Crear {{entity}}',
		editTitle: 'Editar {{entity}}',
		description: 'Completa los campos y guarda los cambios.',
		selectOption: 'Selecciona una opción',
		jsonHelp: 'Objeto JSON válido. Déjalo vacío para borrar los metadatos.',
	},
	preferences: {
		auth: 'Habilitar autenticación',
		'auth.description':
			'Requiere que los usuarios inicien sesión para acceder a la aplicación.',
		'auth.warning':
			'Deshabilitar la autenticación hará la app públicamente accesible.',
		brand: {
			contactEmail: 'Email de contacto',
			'contactEmail.description':
				'Email de contacto principal mostrado a los usuarios.',
		},
	},
	billing: {
		enabled: 'Habilitar Facturación',
		'enabled.description':
			'Interruptor principal del sistema de facturación y suscripciones.',
		'enabled.warning':
			'Deshabilitar la facturación eliminará todas las funciones de suscripción.',
		server: 'Modo del Servidor Polar',
		'server.description':
			'Cambiar entre sandbox (pruebas) y producción (pagos reales).',
		'server.warning':
			'Cambiar a producción procesará pagos reales. Asegúrate de que tu cuenta Polar esté configurada correctamente.',
		'server.sandbox': 'Sandbox (pruebas)',
		'server.production': 'Producción (en vivo)',
		churn: {
			enabled: 'Habilitar prevención de abandono',
			'enabled.description':
				'Permitir acceso durante período de gracia después de cancelación.',
			graceDays: 'Días de gracia',
			'graceDays.description':
				'Número de días que los usuarios mantienen acceso después de cancelar (0-30).',
		},
		portal: {
			enabled: 'Habilitar portal de cliente',
			'enabled.description':
				'Permitir a usuarios gestionar su suscripción vía el portal de Polar.',
			embedded: 'Portal embebido',
			'embedded.description':
				'Renderizar el portal de cliente embebido dentro de la página de facturación.',
		},
		leadMagnet: {
			enabled: 'Habilitar lead magnet',
			'enabled.description':
				'Usar la colección gratuita configurada como lead magnet por defecto en signup y onboarding.',
			'enabled.warning':
				'Si se activa sin un ID de colección válido, los CTA de marketing y el signup pueden degradarse o no apuntar a ningún destino útil.',
			collectionId: 'ID de colección del lead magnet',
			'collectionId.description':
				'ID de colección publicada que se usa en signup, auto-enrollment de onboarding y priorización de marketing.',
			'collectionId.warning':
				'Introduce un ID de colección publicado y válido. Un valor inválido desactiva el auto-enrollment de forma segura.',
			slug: 'Slug del lead magnet',
			'slug.description':
				'Slug operativo opcional para analítica, debugging y futuro linking semántico.',
			internalName: 'Nombre interno del lead magnet',
			'internalName.description':
				'Nombre corto operativo usado en referencias ligeras de UI y contexto para el equipo.',
			ctaLabel: 'Etiqueta CTA del lead magnet',
			'ctaLabel.description':
				'Copy corto de CTA para pricing y otras superficies compactas de captación.',
		},
	},
	permissions: {
		allowInstructorDelete: 'Permitir eliminación por instructor',
		'allowInstructorDelete.description':
			'Permitir a instructores eliminar sus propi{{mainEntityGenderSuffixPlural}} {{mainEntityLabelPluralLower}} y contenido.',
		'allowInstructorDelete.warning':
			'Habilitar esto permite a instructores eliminar contenido permanentemente.',
		allowUserCourseCreation:
			'Permitir creación de {{mainEntityLabelPluralLower}} por usuario',
		'allowUserCourseCreation.description':
			'Permitir a usuarios regulares crear sus propi{{mainEntityGenderSuffixPlural}} {{mainEntityLabelPluralLower}}.',
		requireSubscriptionForExport: 'Requerir suscripción para exportar',
		'requireSubscriptionForExport.description':
			'Solo permitir a suscriptores exportar contenido de {{mainEntityLabelPluralLower}}.',
	},
	features: {
		multiLanguage: 'Habilitar multi-idioma',
		'multiLanguage.description':
			'Permitir a usuarios cambiar entre idiomas disponibles.',
		configStudio: 'Habilitar Config Studio',
		'configStudio.description':
			'Mostrar Config Studio en el panel de admin para gestión de configuración.',
		emailStudio: 'Habilitar Email Studio',
		'emailStudio.description':
			'Mostrar Email Studio para configurar plantillas de correo.',
		course: {
			templates: 'Habilitar plantillas',
			'templates.description':
				'Permitir crear y gestionar plantillas {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
			instances: 'Habilitar instancias',
			'instances.description':
				'Permitir crear instancias de {{mainEntityLabelLower}} desde plantillas.',
			publicPreview: 'Habilitar vista previa pública',
			'publicPreview.description':
				'Mostrar vista previa limitada a usuarios no inscritos.',
			studyPlan: 'Habilitar plan de estudio IA',
			'studyPlan.description':
				'Permitir planes de estudio {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} generados por IA.',
			addModulesAndTopics: 'Habilitar añadir módulos y temas',
			content:
				'Habilitar contenido {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			'content.description':
				'Mostrar la pestaña de contenido en vistas de {{mainEntityLabelPluralLower}}.',
			notes:
				'Habilitar notas {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			'notes.description':
				'Permitir a usuarios tomar notas en {{mainEntityLabelPluralLower}}.',
			diagram:
				'Habilitar diagrama {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
			'diagram.description':
				'Mostrar vista de diagrama visual para {{mainEntityLabelPluralLower}}.',
			duplicate: 'Habilitar duplicar {{mainEntityLabelLower}}',
			'duplicate.description':
				'Permitir duplicar {{mainEntityLabelPluralLower}}.',
			export: 'Habilitar exportar {{mainEntityLabelLower}}',
			'export.description':
				'Permitir exportar contenido de {{mainEntityLabelPluralLower}}.',
			marketing: 'Habilitar marketing',
			'marketing.description':
				'Mostrar funciones de marketing para {{mainEntityLabelPluralLower}}.',
			enableOriginalContentFacet: 'Habilitar faceta de contenido original',
			enableNotesFacet: 'Habilitar faceta de notas',
			enableLlmContent: 'Habilitar contenido LLM',
			'enableLlmContent.description':
				'Permitir contenido generado por IA para {{mainEntityLabelPluralLower}}.',
		},
		topic: {
			content: 'Habilitar contenido del tema',
			'content.description': 'Mostrar el área de contenido en vistas de tema.',
			resources: 'Habilitar recursos del tema',
			'resources.description': 'Mostrar el área de recursos en vistas de tema.',
			notes: 'Habilitar notas del tema',
			'notes.description': 'Permitir a usuarios tomar notas en temas.',
			diagram: 'Habilitar diagrama del tema',
			'diagram.description': 'Mostrar vista de diagrama visual para temas.',
			collectResources: 'Habilitar recolección de recursos',
			'collectResources.description':
				'Permitir recolectar y adjuntar recursos a temas.',
			manage: 'Habilitar gestión del tema',
			enableOriginalContentFacet: 'Habilitar faceta de contenido original',
			enableNotesFacet: 'Habilitar faceta de notas',
			enableLlmContent: 'Habilitar contenido LLM',
			'enableLlmContent.description':
				'Permitir contenido generado por IA para temas.',
		},
		resource: {
			enableOriginalContentFacet: 'Habilitar faceta de contenido original',
			enableNotesFacet: 'Habilitar faceta de notas',
			enableLlmContent: 'Habilitar contenido LLM',
			'enableLlmContent.description':
				'Permitir contenido generado por IA para recursos.',
		},
		collections: {
			enabled: 'Habilitar colecciones',
			'enabled.description':
				'Permitir crear {{collectionEntityLabelPluralLower}} y colecciones de {{mainEntityLabelPluralLower}}.',
		},
	},
	settings: {
		enabled: 'Habilitar ajustes de usuario',
		'enabled.description':
			'Mostrar el área de ajustes donde los usuarios pueden gestionar su cuenta.',
		routes: {
			account: 'Página de cuenta',
			'account.description':
				'Mostrar la página de gestión de cuenta en ajustes.',
			appearance: 'Página de apariencia',
			'appearance.description':
				'Mostrar la página de ajustes de apariencia/tema.',
			display: 'Página de pantalla',
			'display.description': 'Mostrar la página de ajustes de pantalla.',
			notifications: 'Página de notificaciones',
			'notifications.description':
				'Mostrar la página de preferencias de notificaciones.',
			profile: 'Página de perfil',
			'profile.description':
				'Mostrar la página de edición de perfil en ajustes.',
		},
	},
	artifacts: {
		autoGenerate: 'Auto-generar artefactos',
		'autoGenerate.description':
			'Generar artefactos automáticamente cuando se crean temas.',
		allowUserCreation: 'Permitir creación de artefactos por usuario',
		'allowUserCreation.description':
			'Permitir a usuarios crear sus propios artefactos.',
		topic: {
			defaults: 'Artefactos de tema predeterminados',
			'defaults.description':
				'Artefactos a generar por defecto para nuevos temas.',
		},
	},
	course: {
		coverImage: 'Imagen de portada',
		addCover: 'Añadir portada',
		changeCover: 'Cambiar portada',
		defaultTitle: '{{mainEntityLabel}}',
		features: {
			resourceViewer: {
				llm: { enabled: 'LLM en visor de recursos habilitado' },
			},
			progress: {
				enabled:
					'Progreso {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} habilitado',
			},
		},
	},
	featureFlags: {
		course: {
			instances: 'Flag: instancias de {{mainEntityLabelLower}}',
			templates: 'Flag: plantillas de {{mainEntityLabelLower}}',
			publicPreview:
				'Flag: previsualización pública {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
		},
	},
	help: {
		features: {
			multiLanguage: 'Habilita múltiples idiomas y el selector de idioma.',
			configStudio:
				'Muestra Config Studio en admin para editar la configuración.',
			emailStudio:
				'Muestra Email Studio en admin para configurar plantillas y ajustes de correo.',
			course: {
				studyPlan: 'Habilita herramientas de plan de estudio con IA.',
				addModulesAndTopics: 'Permite añadir y organizar módulos y temas.',
				content:
					'Muestra el área de contenido en las pantallas de {{mainEntityLabelPluralLower}}.',
				notes:
					'Habilita las notas de usuario en {{mainEntityLabelPluralLower}}.',
				diagram:
					'Habilita la vista de diagrama para {{mainEntityLabelPluralLower}}.',
				duplicate:
					'Permite duplicar {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
				export:
					'Permite exportar {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
				templates:
					'Permite crear y gestionar plantillas {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
				instances:
					'Permite crear instancias de {{mainEntityLabelLower}} desde plantillas.',
				marketing:
					'Habilita funcionalidades de marketing para {{mainEntityLabelPluralLower}}.',
				publicPreview:
					'Expone una vista previa pública limitada del contenido seleccionado.',
				enableOriginalContentFacet:
					'Muestra la faceta de contenido original en paneles laterales.',
				enableNotesFacet: 'Muestra la faceta de notas en paneles laterales.',
				enableLlmContent:
					'Expone contenido generado por LLM donde esté disponible.',
			},
			topic: {
				content: 'Muestra el área de contenido en pantallas de tema.',
				resources: 'Habilita el área de recursos en pantallas de tema.',
				notes: 'Habilita notas de usuario en pantallas de tema.',
				diagram: 'Habilita la vista de diagrama para temas.',
				collectResources: 'Permite recolectar y adjuntar recursos al tema.',
				manage: 'Habilita herramientas de gestión del tema.',
				enableOriginalContentFacet:
					'Muestra la faceta de contenido original para temas.',
				enableNotesFacet: 'Muestra la faceta de notas para temas.',
				enableLlmContent: 'Expone contenido generado por LLM para temas.',
			},
			resource: {
				enableOriginalContentFacet:
					'Muestra la faceta de contenido original para recursos.',
				enableNotesFacet: 'Muestra la faceta de notas para recursos.',
				enableLlmContent: 'Expone contenido generado por LLM para recursos.',
			},
			collections: {
				enabled:
					'Habilita {{collectionEntityLabelPluralLower}} y funcionalidades relacionadas. Permite agrupar {{mainEntityLabelPluralLower}} en {{collectionEntityLabelPluralLower}} secuenciales o colecciones temáticas.',
			},
		},
		preferences: {
			auth: 'Requiere iniciar sesión para acceder a la aplicación.',
		},
		billing: {
			enabled:
				'Interruptor principal para suscripciones y flujos de facturación.',
			polar:
				'Habilita la integración con Polar usada para checkout y sincronización de suscripciones.',
			subscription:
				'Expone los flujos de suscripción y las comprobaciones de entitlement.',
			server:
				'Usa sandbox para pruebas y producción para cobros reales. Las variables de entorno siguen teniendo prioridad.',
			portal: {
				enabled: 'Activa el acceso al portal de cliente para facturación.',
				embedded:
					'Renderiza el portal de cliente embebido en ajustes en lugar de enlazar fuera.',
			},
			churn: {
				enabled:
					'Permite un periodo de gracia tras la cancelación antes de retirar por completo el acceso premium.',
				graceDays:
					'Número de días que dura el periodo de gracia después de una cancelación.',
			},
			leadMagnet: {
				enabled:
					'Activa o desactiva el flujo del lead magnet sin borrar su metadata.',
				collectionId:
					'ID de colección publicada usado como lead magnet canónico. Este valor alimenta signup, onboarding y los CTA de marketing.',
				slug: 'Slug corto opcional para operaciones, analítica y debugging.',
				internalName:
					'Nombre corto operativo para la colección lead magnet activa.',
				ctaLabel:
					'Etiqueta CTA opcional usada en pricing y otras superficies compactas de captación.',
			},
		},
		settings: {
			enabled: 'Habilita el área de ajustes de usuario.',
			routes: {
				account: 'Muestra la página de cuenta en ajustes.',
				appearance: 'Muestra la página de apariencia en ajustes.',
				display: 'Muestra la página de pantalla en ajustes.',
				notifications: 'Muestra la página de notificaciones en ajustes.',
				profile: 'Muestra la página de perfil en ajustes.',
			},
		},
		appSidebar: {
			courses: 'Mostrar {{mainEntityLabelPluralLower}} en la barra lateral.',
			topics: 'Mostrar temas en la barra lateral.',
			templates: 'Mostrar plantillas en la barra lateral.',
			favoriteCourses: 'Mostrar sección de favoritos en la barra lateral.',
			searchContent:
				'Mostrar entrada de búsqueda de contenido en la barra lateral.',
			support: 'Mostrar soporte en la barra lateral.',
			profile: 'Mostrar perfil en la barra lateral.',
			notes: 'Mostrar notas en la barra lateral.',
		},
		course: {
			features: {
				resourceViewer: {
					llm: {
						enabled:
							'Habilita el asistente LLM en el visor de recursos {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
					},
				},
				progress: {
					enabled:
						'Activa y muestra el progreso {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}.',
				},
			},
		},
	},
	users: {
		title: 'Usuarios',
		subtitle: 'Gestionar usuarios del sistema',
		columns: {
			name: 'Nombre',
			email: 'Correo',
			roles: 'Roles',
			createdAt: 'Creado',
			lastLogin: 'Último acceso',
			status: 'Estado',
		},
		learning: {
			title: 'Aprendizaje',
			description:
				'Resumen solo para admin del acceso a {{mainEntityLabelPluralLower}} y {{collectionEntityLabelPluralLower}}.',
			loading: 'Cargando datos de aprendizaje...',
			empty:
				'No hay actividad en {{mainEntityLabelPluralLower}} ni {{collectionEntityLabelPluralLower}}.',
			enrolledCollections: '{{collectionEntityLabelPlural}}',
			enrolledCourses: '{{mainEntityLabelPlural}} de catálogo',
			personalCourses: '{{mainEntityLabelPlural}} personales',
			collectionProgress: '{{completed}}/{{total}}',
			unpublished: 'No publicado',
			personalItem: '{{mainEntityLabel}} personal',
			sources: {
				pro: 'Pro',
				courseFree: 'Gratis',
				collectionOpen: '{{collectionEntityLabel}} abierto',
				budget: 'Crédito',
				grant: 'Concesión',
			},
		},
		detail: {
			backToUsers: 'Volver a usuarios',
			suspendedBanner: 'Esta cuenta fue suspendida el {{date}}.',
			suspendedReason: 'Motivo: {{reason}}',
			actions: {
				impersonate: 'Suplantar',
				more: 'Más acciones',
				suspend: 'Suspender cuenta',
				reactivate: 'Reactivar cuenta',
				delete: 'Eliminar cuenta',
			},
			suspendDialog: {
				title: '¿Suspender esta cuenta?',
				description:
					'Se cerrará la sesión del usuario en todos los dispositivos y no podrá iniciar sesión hasta que se reactive la cuenta.',
				reasonLabel: 'Motivo (opcional)',
			},
			reactivateDialog: {
				title: '¿Reactivar esta cuenta?',
				description: 'El usuario podrá volver a iniciar sesión.',
			},
			deleteDialog: {
				title: '¿Eliminar esta cuenta de forma permanente?',
				description:
					'Se eliminará el usuario y todos sus datos. Esta acción no se puede deshacer.',
			},
			tabs: {
				overview: 'Resumen',
				learning: 'Aprendizaje',
				billing: 'Suscripción',
				activity: 'Actividad',
			},
			metrics: {
				courses: 'Inscripciones a cursos',
				coursesDescription: 'Cursos de catálogo a los que se ha unido.',
				collections: 'Colecciones',
				collectionsDescription: 'Rutas de aprendizaje en curso o completadas.',
				topicsCompleted: 'Temas completados',
				topicsCompletedDescription:
					'Completados frente al total de temas con progreso registrado.',
				activeSessions: 'Sesiones activas',
				activeSessionsDescription: 'Última actividad: {{date}}',
			},
			identity: {
				title: 'Identidad',
				description: 'Datos principales de la cuenta.',
			},
			security: {
				title: 'Acceso y seguridad',
				description: 'Métodos de acceso, sesiones y estado de la cuenta.',
				passkeys: 'Passkeys',
				activeSessions: 'Sesiones activas',
				lastSeen: 'Última actividad de sesión',
			},
			learning: {
				collections: 'Colecciones',
				catalogCourses: 'Inscripciones de catálogo',
				personalCourses: 'Cursos personales',
				startedAt: 'Iniciado el {{date}}',
			},
			billing: {
				empty: 'Este usuario no tiene historial de suscripciones.',
				cancelsAtPeriodEnd: 'Se cancela al final del periodo',
				period: 'Periodo actual: {{start}} → {{end}}',
				trial: 'Prueba',
				canceledAt: 'Cancelada',
				startedAt: 'Suscrito desde',
				providerId: 'ID de suscripción del proveedor',
				plans: {
					monthly: 'Plan mensual',
					annual: 'Plan anual',
				},
			},
			activity: {
				title: 'Actividad reciente',
				description:
					'{{actions}} interacciones y {{topics}} temas con progreso en total.',
				empty: 'No hay actividad reciente registrada para este usuario.',
				actionLabel: '{{action}} en {{target}}',
				topicCompleted: 'Completó el tema «{{title}}»',
				topicProgress: 'Avanzó en el tema «{{title}}»',
			},
		},
	},
	roles: {
		title: 'Roles',
		subtitle: 'Gestionar roles y permisos de usuario',
		columns: {
			name: 'Nombre',
			description: 'Descripción',
			permissions: 'Permisos',
			users: 'Usuarios',
			createdAt: 'Creado',
		},
	},
	// Etiquetas para logging.action usadas por el Registry de Configuración
	logging: {
		level: 'Nivel de logs',
		categories: {
			router: 'Router',
			handler: 'Handler',
			billing: 'Billing',
			webhook: 'Webhook',
			conditions: 'Condiciones',
			loaders: 'Loaders',
		},
		levels: {
			trace: 'trace',
			debug: 'debug',
			info: 'info',
			warn: 'warn',
			error: 'error',
		},
		action: {
			enabled: 'Habilitar logging de acciones',
			development: {
				enabled: 'Habilitado (desarrollo)',
				minLevel: 'Nivel mínimo (desarrollo)',
				contexts: 'Contextos (array)',
				features: {
					requestDetails: 'Detalles de la solicitud',
					responseDetails: 'Detalles de la respuesta',
					formDataLogging: 'Registrar form data',
					errorStackTraces: 'Stack traces de error',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Errores de validación',
					handlerTiming: 'Tiempos del handler',
				},
			},
			production: {
				enabled: 'Habilitado (producción)',
				minLevel: 'Nivel mínimo (producción)',
				contexts: 'Contextos (array)',
				features: {
					requestDetails: 'Detalles de la solicitud',
					responseDetails: 'Detalles de la respuesta',
					formDataLogging: 'Registrar form data',
					errorStackTraces: 'Stack traces de error',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Errores de validación',
					handlerTiming: 'Tiempos del handler',
				},
			},
			testing: {
				enabled: 'Habilitado (pruebas)',
				minLevel: 'Nivel mínimo (pruebas)',
				contexts: 'Contextos (array)',
				features: {
					requestDetails: 'Detalles de la solicitud',
					responseDetails: 'Detalles de la respuesta',
					formDataLogging: 'Registrar form data',
					errorStackTraces: 'Stack traces de error',
					llmRequestResponse: 'LLM request/response',
					validationErrors: 'Errores de validación',
					handlerTiming: 'Tiempos del handler',
				},
			},
		},
	},
	emailStudio: {
		title: 'Email Studio',
		description: 'Configurar plantillas y ajustes de correo',
		tabs: {
			templates: 'Plantillas',
			settings: 'Ajustes',
		},
		actions: {
			save: 'Guardar cambios',
			snapshot: 'Crear snapshot',
			showDiff: 'Mostrar diferencias',
			hideDiff: 'Ocultar diferencias',
		},
		messages: {
			saved: 'Cambios guardados en vivo',
			promoted: 'Snapshot registrado',
		},
		errors: {
			invalidJson: 'JSON inválido',
		},
		model: {
			title: 'Modelo de configuración en vivo',
			description:
				'Guardar actualiza la configuración activa de esta marca inmediatamente. Los snapshots crean puntos de rollback en el historial sin mover datos entre entornos.',
		},
		state: {
			title: 'Estado actual',
			description:
				'Usa el valor en vivo como base de edición y registra snapshots antes de cambios sensibles.',
			currentValue: 'Valor en vivo actual',
			currentVersion: 'Versión {{version}}',
			latestSnapshot: 'Último snapshot',
			snapshotVersion: 'Versión {{version}}',
			noSnapshot: 'Todavía no hay ningún snapshot',
		},
		help: {
			templates:
				'Configura plantillas de correo, asuntos y sobrescrituras por idioma. Usa claves i18n para asuntos.',
			settings:
				'Configura ajustes de entrega de correo, proveedor y direcciones predeterminadas.',
		},
		placeholder: {
			templates:
				'Ingresa la configuración de plantillas de correo como JSON...',
			settings: 'Ingresa la configuración de ajustes de correo como JSON...',
		},
		tips: {
			title: 'Consejos:',
			subjects: 'Usa claves i18n para asuntos (ej., "Emails.verify.subject")',
			components:
				'Los nombres de componentes deben coincidir con archivos en app/emails/templates/',
			validation: 'El JSON será validado antes de guardar',
			liveModel:
				'Guardar actualiza la configuración en vivo. Crea un snapshot antes de cambios delicados si quieres un punto de rollback.',
		},
		diff: {
			title: 'Valor en vivo vs borrador actual',
			current: 'Valor en vivo',
			draft: 'Borrador',
		},
		editor: {
			rawJsonLabel: 'JSON de plantillas (valor en vivo)',
			settingsJsonLabel: 'JSON de ajustes (valor en vivo)',
		},
	},
	emailSequences: {
		title: 'Secuencias de email',
		description:
			'Monitoriza el estado de entrega, reintentos y referencias del proveedor.',
		empty: 'No se encontraron entregas de secuencias con los filtros actuales.',
		statuses: {
			pending: 'Pendiente',
			processing: 'Procesando',
			sent: 'Enviado',
			skipped: 'Omitido',
			error: 'Error',
			canceled: 'Cancelado',
		},
		filters: {
			status: 'Estado',
			allStatuses: 'Todos los estados',
			brand: 'Marca',
			allBrands: 'Todas las marcas',
			sequence: 'Secuencia',
			allSequences: 'Todas las secuencias',
			limit: 'Límite',
			apply: 'Aplicar',
		},
		table: {
			email: 'Email',
			brand: 'Marca',
			sequence: 'Secuencia',
			step: 'Paso',
			status: 'Estado',
			scheduledFor: 'Programado para',
			retryCount: 'Reintentos',
			providerMessageId: 'Mensaje proveedor',
			lastError: 'Último error',
			actions: 'Acciones',
		},
		actions: {
			retry: 'Reintentar',
		},
	},
	courses: {
		title: '{{mainEntityLabelPlural}}',
		subtitle: 'Gestionar {{mainEntityLabelPluralLower}} y plantillas',
		templateSummary: 'Resumen de plantillas',
		totalTemplates: 'Plantillas',
		totalInstances: 'Instancias',
		totalCourses: 'Total de {{mainEntityLabelPluralLower}}',
		table: {
			status: {
				published: 'Publicado',
				unpublished: 'No publicado',
				template: 'Plantilla',
				collection: '{{collectionEntityLabel}}',
			},
			readiness: {
				structureOnly: 'Solo estructura',
				blockingSlice: 'Blocking slice',
				fullBlueprints: 'Full blueprints',
				ready: 'Ready',
				deferred: 'Diferido',
				failed: 'Fallido',
			},
		},
		filters: {
			owner: 'Propietario',
			allOwners: 'Todos los propietarios',
			uncategorized: 'Sin categoría',
			includeChildCourses: 'Mostrar cursos hijo',
		},
		columns: {
			title: 'Título',
			category: 'Categoría',
			owner: 'Propietario',
			isTemplate: 'Plantilla',
			isPublished: 'Publicado',
			modules: 'Módulos',
			enrollments: 'Inscripciones',
			createdAt: 'Creado',
		},
		actions: {
			createWizard: 'Crear {{mainEntityLabelPluralLower}}',
			enrichWizard: 'Enriquecer {{mainEntityLabelPluralLower}}',
			bulkCreate: 'Creación masiva',
			viewCreationStatus: 'Estado de creación',
			convertToCollection: 'Convertir a colección',
		},
		dialogs: {
			createWizard: {
				title:
					'Crear {{mainEntityLabelPluralLower}} e {{collectionEntityLabelPluralLower}}',
				description:
					'Configura y ejecuta un flujo de creación de {{mainEntityLabelPluralLower}} e {{collectionEntityLabelPluralLower}}',
				step: 'Paso',
				useCase: 'Elige el método de creación',
				byName: 'Crear {{mainEntityLabelLower}} individual',
				byNameDescription:
					'Crea {{mainEntityLabelLower}} con título y categoría. Perfecto para creación individual.',
				fromCategory: 'Desde categoría',
				collection: 'Crear {{collectionEntityLabelLower}}',
				collectionDescription:
					'Crea {{collectionEntityArticleLower}} {{collectionEntityLabelLower}} principal con múltiples {{mainEntityLabelPluralLower}}. Ideal para {{collectionEntityLabelPluralLower}} o series.',
				bulkFromSubcategories:
					'Creación masiva de {{mainEntityLabelPluralLower}}',
				bulkFromSubcategoriesDescription:
					'Crea múltiples {{mainEntityLabelPluralLower}} desde subcategorías. Mejor para poblar el catálogo rápidamente.',
				bulkCollections: 'Bulk Collections',
				bulkCollectionsDescription:
					'Crea múltiples colecciones a la vez a partir de una definición JSON. Importa colecciones generadas por el skill o pega las tuyas.',
				recommended: 'Recomendado',
				presetLabel: 'Intención de creación',
				presetSelect: 'Seleccionar intención de creación',
				presetHelp:
					'Este preset agrupa valores por defecto del flujo, como la generación de estructura, el plan de estudio y el diagrama.',
				preparationModeLabel: 'Modo de preparación',
				preparationModeReviewLabel: 'Modo de preparación',
				preparationModeSelect: 'Seleccionar modo de preparación',
				preparationModeSummaryTitle: 'Resultado de preparación',
				workflowSummaryLabel: 'Resumen del flujo',
				impactPreview: 'Vista previa del impacto',
				executionProfile: 'Perfil de ejecución',
				estimatedDuration: 'Duración estimada',
				modulesAndTopicsSummary: 'Módulos y temas',
				booleanYes: 'Sí',
				booleanNo: 'No',
				preparationModeScope: {
					bulkCollections:
						'En colecciones, este modo se aplica a los cursos hijo; el contenedor de la colección no materializa topics propios.',
					bulkFromSubcategories:
						'En creación masiva, este modo se aplicará a cada curso generado desde las subcategorías seleccionadas.',
				},
				preparationModes: {
					structureOnly: {
						label: 'Solo estructura',
						description:
							'Crea el shell del curso con módulos y temas, sin preparar blueprints de topic ni recopilar recursos.',
						outcome:
							'Ideal para shells, borradores manuales o trabajo de catálogo donde solo quieres estructura.',
					},
					blockingSlice: {
						label: 'Blocking slice',
						description:
							'Prepara solo el primer topic para que el curso sea usable de inmediato mientras el resto queda diferido.',
						outcome:
							'Útil para slices de onboarding o validaciones donde solo hace falta preparar el topic inicial.',
					},
					fullBlueprints: {
						label: 'Full blueprints',
						description:
							'Prepara facet blueprints para todos los topics y deja los recursos diferidos.',
						outcome:
							'Buen equilibrio cuando quieres todos los topics listos a nivel blueprint sin declarar full readiness todavía.',
					},
					fullReadiness: {
						label: 'Full readiness',
						description:
							'Prepara todos los blueprints de topic y recopila recursos desde el inicio.',
						outcome:
							'Úsalo cuando el curso deba sentirse completamente listo al entrar.',
					},
					contentReady: {
						label: 'Content ready',
						description:
							'Prepara blueprints, recopila recursos y genera todos los artifacts de contenido para cada topic. El learner entra a un curso completamente consumible.',
						outcome:
							'Ideal para cursos editoriales donde el learner debe encontrar contenido completo desde el inicio.',
					},
				},
				presets: {
					study_map: {
						label: 'Mapa de estudio',
						description:
							'Paquete inicial para creación individual. Genera módulos, topics, el plan de estudio y activa el diagrama del curso por defecto.',
					},
					fast_shell: {
						label: 'Shell rápido',
						description:
							'Paquete inicial para placeholders rápidos. Mantiene el curso como shell sin plan de estudio, enriquecimiento ni diagrama.',
					},
					bulk_editorial_draft: {
						label: 'Borrador editorial masivo',
						description:
							'Paquete inicial para revisión editorial. Genera módulos, topics y metadatos dejando el diagrama apagado por defecto.',
					},
					collection_with_child_structures: {
						label: 'Colección con estructuras hijas',
						description:
							'Paquete inicial para colecciones que deben generar estructuras de estudio en los cursos hijo.',
					},
					collection_shells_only: {
						label: 'Solo shells de colección',
						description:
							'Paquete inicial para colecciones shell que enlazan hijos sin generar sus estructuras internas.',
					},
				},
				phase1Info:
					'Todos los {{mainEntityLabelPluralLower}} se crearán solo con estructura fase 1 (metadatos + plan de estudio)',
				titleLabel: 'Título',
				titlePlaceholder:
					'Ingresa el título {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
				titleHelp: 'Mínimo 3 caracteres requeridos',
				slug: 'Slug (opcional)',
				categories: 'Categorías',
				categoriesPlaceholder: 'Categoría-1, categoría-2',
				categoriesHelp: 'Slugs de categorías separados por comas',
				descriptionLabel: 'Descripción (opcional)',
				descriptionPlaceholder:
					'Descripción corta para este {{collectionEntityLabelLower}}',
				categorySlug: 'Slug de categoría',
				depth: 'Profundidad (opcional)',
				parentTitle: 'Título padre',
				parentSlug: 'Slug padre (opcional)',
				children: 'Hijos (un título por línea)',
				childrenStructureJsonLabel: 'JSON de estructura completa',
				childrenStructureJsonHelp:
					'Define módulos y cursos de forma explícita. Hints opcionales por curso: descriptionHint, outcomeHint, level, slug.',
				childrenStructureJsonParseError: 'Formato JSON inválido.',
				childrenStructureJsonFormatError:
					'El JSON debe ser un objeto con modules[] o courses[], o un array de módulos.',
				childrenStructureCount:
					'Conteo actual: {{count}} curso{{pluralSuffix}}',
				ownerId: 'ID del propietario',
				language: 'Idioma',
				selectLanguage: 'Seleccionar idioma',
				accessLevel: 'Nivel de acceso',
				selectAccess: 'Seleccionar acceso',
				courseOptions:
					'Opciones {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
				courseOptionsDescription:
					'Configura los ajustes básicos {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
				courseTypeKey: {
					label: 'Tipo de curso',
					select: 'Seleccionar tipo de curso',
					options: {
						brand_default: 'Por defecto de marca',
						blank: 'En blanco',
						architect_profile: 'Perfil de arquitecto',
						case_study: 'Caso de estudio',
						concept_deep_dive: 'Concepto en profundidad',
						tool_tutorial: 'Tutorial de herramienta',
						professional_guide: 'Guía profesional',
					},
					help: 'En blanco desactiva el sesgo de marca y editorial de la taxonomía durante la generación con IA.',
				},
				collectionTypeKey: {
					label: 'Tipo de colección',
					select: 'Seleccionar tipo de colección',
					help: 'Define el estilo narrativo de la colección y sus valores por defecto de secuencialidad.',
				},
				collectionStructureMode: {
					label: 'Modo de estructura',
					select: 'Seleccionar modo de estructura',
					options: {
						ai_generate_structure: 'Estructura generada por IA',
						manual_titles_only: 'Solo títulos manuales',
						manual_full_structure: 'Estructura manual completa',
					},
				},
				level: 'Nivel',
				selectLevel: 'Seleccionar nivel',
				publish: 'Publicar',
				idempotencyKey: 'Clave de idempotencia',
				generateKey: 'Generar',
				keyHelp:
					'Usa esta clave para reintentar o reanudar el proceso de creación de forma segura.',
				review: 'Revisar',
				creationOptions: 'Creación fase 1 (solo estructura)',
				createModulesAndTopics: 'Crear módulos y temas',
				createDiagramArtifact: 'Crear diagrama',
				createDiagramArtifactHelp:
					'Genera un artefacto de curso de fase 1. No cambia la preparación de los topics.',
				enrichMetadata: 'Enriquecer metadatos',
				generateStudyPlan: 'Generar plan de estudio',
				phase1Note: 'Los recursos y artefactos se añadirán en la fase 2',
				run: 'Ejecutar',
				bulkCollectionsSuccess: '{{count}} colecciones creadas correctamente.',
				bulkCollectionsFailure: 'La creación de colecciones ha fallado.',
				bulkCollectionsPartialFailure:
					'{{succeeded}} creadas, {{failed}} fallidas.',
				bulkCollectionsRequestFailed:
					'La petición ha fallado con estado {{status}}.',
				bulkCollectionsNetworkError: 'Error de red.',
				bulkCollectionsSummary: {
					collections: 'Colecciones',
					totalModules: 'Total de módulos',
					totalCourses: 'Total de cursos',
					collectionBreakdown: 'Desglose de colecciones',
					collectionStats: '{{modules}} módulos, {{courses}} cursos',
					creationOptions: 'Opciones de creación',
					note: 'Cada colección se creará secuencialmente. Esto puede tardar varios minutos según el número de colecciones y cursos.',
				},
				success:
					'Creación {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} iniciada/completada.',
				result: 'Resultado',
				error: 'Error',
				// Bulk review strings
				bulkReviewSummary:
					'{{Count}} {{mainEntityLabelPluralLower}} serán cread{{mainEntityGenderSuffixPlural}} desde {{parent}} subcategorías',
				selectedSubcategories: 'Subcategorías seleccionadas',
				createArtifacts: 'Crear artefactos',
				bulkOperationNote:
					'La creación masiva de {{mainEntityLabelPluralLower}} puede tardar varios minutos. Puedes monitorear el progreso en el modal que aparece después de enviar.',
				parent: 'Padre',
				// Collection strings
				isSequential: 'Colección secuencial',
				isSequentialHelp:
					'Los estudiantes deben completar los cursos en orden.',
				createChildrenContent: 'Crear contenido de hijos',
				linkChildrenToParent: 'Vincular hijos al padre',
				category: 'Categoría',
				selectCategory: 'Selecciona una categoría',
			},
			bulkCreate: {
				title: 'Creación masiva de {{mainEntityLabelPluralLower}}',
				description:
					'Pega un array JSON o una lista de títulos separados por saltos de línea. El Owner ID se aplicará por defecto.',
				ownerId: 'Owner ID',
				input: 'Entrada de {{mainEntityLabelPluralLower}}',
				preview: 'Vista previa',
				run: 'Ejecutar',
				result: 'Resultado',
			},
			convertToCollection: {
				description:
					'Convertir "{{course}}" en {{collectionEntityArticleLower}} {{collectionEntityLabelLower}} o colección',
				makeSequential:
					'Hacer secuencial ({{collectionEntityLabelLower}} guiado)',
				sequentialHelp:
					'Los {{mainEntityLabelPluralLower}} se presentarán en un orden específico como {{collectionEntityArticleLower}} {{collectionEntityLabelLower}} estructurad{{collectionEntityGenderSuffix}}.',
				collectionHelp:
					'Los {{mainEntityLabelPluralLower}} se pueden tomar en cualquier orden como colección temática.',
			},
			enrichWizard: {
				title: 'Enriquecer {{mainEntityLabelPluralLower}}',
				description:
					'Elige el tipo de enriquecimiento a aplicar a tus {{mainEntityLabelPluralLower}}',
				steps: {
					select: 'Seleccionar enriquecimiento',
					confirm: 'Confirmar enriquecimiento',
					progress: 'Enriqueciendo {{mainEntityLabelPluralLower}}',
					results: 'Enriquecimiento completado',
				},
				selectDescription:
					'Selecciona el tipo de enriquecimiento a aplicar a tus {{mainEntityLabelPluralLower}}.',
				unsplash: {
					title: 'Imágenes de portada de unsplash',
					description:
						'Asignar imágenes de portada de unsplash a {{mainEntityLabelPluralLower}} sin imágenes',
					features: {
						discover:
							'Descubre automáticamente {{mainEntityLabelPluralLower}} que necesitan imágenes',
						batch:
							'Procesa hasta 50 {{mainEntityLabelPluralLower}} por ejecución',
						rateLimit: 'Respeta los límites de la API',
					},
				},
				educational: {
					title: 'Preparación educativa',
					description:
						'Crea blueprints de facetas de tema y recursos curados para un curso o para los cursos hijo directos de una colección.',
					targetLabel: 'Curso o colección objetivo',
					targetPlaceholder: 'Selecciona un curso o una colección',
					selectedTarget: 'Seleccionado: {{title}}',
					noTargets: 'Todavía no hay cursos ni colecciones disponibles.',
					preparationModeLabel: 'Modo de preparación',
					preparationModeSelected: 'Modo de preparación: {{mode}}',
					targetKinds: {
						collection: 'Colección',
					},
					preparationModes: {
						blockingSlice: {
							label: 'Blocking slice',
							description:
								'Prepara solo el slice inicial para desbloquear el primer uso lo antes posible.',
							outcome:
								'Mantiene el comportamiento rápido heredado y deja el resto del curso para preparación posterior.',
						},
						fullBlueprints: {
							label: 'Full blueprints',
							description:
								'Deja todos los topics con facet blueprints listos para trabajo bajo demanda.',
							outcome:
								'Buen equilibrio para lead magnets o cursos donde cada learner construirá su propia experiencia.',
						},
						fullReadiness: {
							label: 'Full readiness',
							description:
								'Deja todos los topics con blueprints y recursos ya recopilados y curados.',
							outcome:
								'Es el estándar de ready recomendado para colecciones y cursos que deben sentirse listos al entrar.',
						},
						contentReady: {
							label: 'Content ready',
							description:
								'Prepara blueprints, recopila recursos y genera todos los artifacts de contenido. El learner entra a un curso completamente consumible.',
							outcome:
								'Ideal para cursos editoriales donde todo el contenido debe estar listo antes de que entre cualquier learner.',
						},
					},
					features: {
						single: 'Funciona para un curso o una colección',
						children:
							'Las ejecuciones sobre colecciones solo afectan a los cursos hijo directos',
						skip: 'Los cursos hijo ya enriquecidos se omiten automáticamente',
					},
					confirm: {
						singleAlert:
							'Esta ejecución preparará el curso seleccionado según el modo que has elegido.',
						collectionAlert:
							'Esta ejecución enriquecerá cursos hijo directos, no el contenedor de la colección.',
						steps: {
							resolve:
								'Resolver un curso único o los cursos hijo directos de la colección seleccionada',
							blueprints:
								'Crear blueprints de facetas de tema donde hagan falta',
							resources:
								'Recopilar y curar recursos temáticos para cada curso objetivo',
							skip: 'Omitir los cursos hijo directos que ya cumplan el modo de preparación solicitado',
						},
					},
					metrics: {
						totalTargeted: 'Total objetivo',
						toEnrich: 'Se prepararán',
						skipped: 'Ya preparados',
					},
					results: {
						skippedLabel: 'Omitidos por ya estar preparados',
						description: 'Revisa los cursos omitidos, exitosos y fallidos.',
					},
				},
				confirm: {
					alert:
						'Esto buscará y asignará imágenes de portada de unsplash a todos los {{mainEntityLabelPluralLower}} que actualmente no tengan una. El proceso manejará hasta 50 {{mainEntityLabelPluralLower}} en este lote.',
					whatWillHappen: 'Qué sucederá:',
					steps: {
						discover:
							'Descubrir {{mainEntityLabelPluralLower}} sin imágenes de portada',
						extract:
							'Extraer palabras clave de los metadatos {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
						search: 'Buscar imágenes coincidentes en unsplash',
						create: 'Crear artefactos con atribución completa',
						update:
							'Actualizar urls de imágenes de portada {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
					},
				},
				progress: {
					title: 'Enriqueciendo {{mainEntityLabelPluralLower}}...',
					description:
						'Buscando imágenes y actualizando {{mainEntityLabelPluralLower}}',
				},
				results: {
					successful: 'Exitosos',
					failed: 'Fallidos',
					remaining: 'Restantes',
					successfullyEnriched: 'Enriquecidos exitosamente',
					rateLimitWarning: '¡Límite de tasa alcanzado!',
					rateLimitDescription:
						'Se excedió el límite de la API de unsplash. Por favor espera antes de ejecutar otro lote de enriquecimiento.',
				},
				buttons: {
					cancel: 'Cancelar',
					next: 'Siguiente',
					back: 'Atrás',
					startEnrichment: 'Iniciar enriquecimiento',
					processing: 'Procesando...',
					enrichMore: 'Enriquecer más',
					done: 'Listo',
				},
				run: 'Ejecutar',
				success:
					'Enriquecimiento {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} iniciado/completado.',
				result: 'Resultado',
				error: 'Error',
			},
			status: {
				title: 'Estado de creación',
				description:
					'Consulta el estado por clave de idempotencia o activa el polling.',
				key: 'Clave de idempotencia',
				fetch: 'Consultar',
				start: 'Iniciar',
				stop: 'Detener',
				result: 'Resultado',
			},
		},
		languages: {
			es: 'Español',
			en: 'Inglés',
		},
		access: {
			free: 'Gratis',
			subscription: 'Suscripción',
			enrollment: 'Inscripción',
			preview: 'Vista previa',
		},
		creation: {
			new: {
				title: 'Crear nuev{{mainEntityGenderSuffix}} {{mainEntityLabelLower}}',
				description:
					'Sigue los pasos a continuación para crear {{mainEntityArticleLower}} {{mainEntityLabelLower}} con asistencia de IA',
			},
			list: {
				title: 'Creaciones recientes',
				description:
					'Monitorear y administrar el historial de creación de {{mainEntityLabelPluralLower}}',
			},
			wizard: {
				step1: 'Punto de entrada',
				step2: 'Detalles',
				step3: 'Revisión',
			},
		},
	},
	categories: {
		title: 'Categorías',
		subtitle: 'Gestionar categorías de {{mainEntityLabelPluralLower}}',
		actions: {
			generateTaxonomy: 'Generar taxonomía',
			import: 'Importar categorías',
			enrichEditorialWizard: 'Enriquecer categorías',
		},
		onlyParents: 'Solo categorías padre',
		dialogs: {
			enrichWizard: {
				title: 'Enriquecer categorías',
				description:
					'Elige el tipo de enriquecimiento a aplicar a tus categorías',
				steps: {
					select: 'Seleccionar enriquecimiento',
					confirm: 'Confirmar enriquecimiento',
					progress: 'Enriqueciendo categorías',
					results: 'Enriquecimiento completado',
				},
				selectDescription:
					'Selecciona el tipo de enriquecimiento a aplicar a tus categorías.',
				editorial: {
					title: 'Metadatos editoriales',
					description:
						'Genera registro/tono editorial y tecnicidad para categorías y subcategorías, para componer prompts.',
				},
				confirm: {
					alert:
						'Esto generará metadatos editoriales para subcategorías que aún no los tengan. El proceso maneja un lote pequeño por ejecución.',
					whatWillHappen: 'Qué sucederá:',
					steps: {
						discover: 'Detectar subcategorías sin metadatos editoriales',
						generate:
							'Generar registro/tecnicidad y reglas de escritura con LLM',
						store: 'Guardar el resultado en category.metadata.editorial',
					},
				},
				progress: {
					title: 'Enriqueciendo categorías...',
					description:
						'Generando metadatos editoriales y actualizando categorías',
				},
				results: {
					successful: 'Exitosos',
					failed: 'Fallidos',
					remaining: 'Restantes',
					successfullyEnriched: 'Enriquecidos exitosamente',
				},
				buttons: {
					cancel: 'Cancelar',
					next: 'Siguiente',
					back: 'Atrás',
					startEnrichment: 'Iniciar enriquecimiento',
					processing: 'Procesando...',
					enrichMore: 'Enriquecer más',
					done: 'Listo',
				},
			},
		},
		taxonomy: {
			dialog: {
				title: 'Generar taxonomía de categorías',
				mode: {
					bulk: {
						title: 'Importación masiva',
						description:
							'Introduce o pega manualmente una lista de categorías y subcategorías.',
					},
					llm: {
						title: 'Crear con IA',
						description:
							'Genera una estructura de taxonomía completa usando IA.',
						blocked:
							'La generación con IA solo está disponible para la creación inicial de la taxonomía. Usa importación masiva para añadir más categorías.',
					},
				},
				fields: {
					mode: 'Modo de generación',
					subjectArea: 'Área temática',
					subjectAreaPlaceholder: 'Ej. Desarrollo web, ciencia de datos',
					numCategories: 'Categorías principales',
					minSubcategories: 'Mín. Subcategorías',
					maxSubcategories: 'Máx. Subcategorías',
					language: 'Idioma',
					hierarchyText: 'Revisar y editar jerarquía',
					hierarchyHelp:
						'Edita el texto a continuación para modificar la taxonomía. Usa guiones para subcategorías.',
					hierarchyPlaceholder:
						'Categoría 1\n- subcategoría 1\n- subcategoría 2\ncategoría 2\n- subcategoría A',
				},
				description: {
					step1: 'Configura los ajustes de generación',
					step2: 'Revisa y edita la taxonomía generada',
				},
				steps: {
					configuration: 'Configuración',
					review: 'Revisar y crear',
				},
				buttons: {
					generatePreview: 'Generar vista previa',
				},
				validation: {
					subjectAreaRequired: 'El área temática es obligatoria',
					minMaxInvalid:
						'El mínimo de subcategorías debe ser menor o igual al máximo',
				},
				errors: {
					parsingFailed: 'Error al analizar la jerarquía de categorías',
					invalidCharacters:
						'Los nombres de categorías solo pueden contener letras, números, espacios, guiones, ampersands y paréntesis',
					emptyName: 'El nombre de la categoría no puede estar vacío',
					duplicateName: 'Se encontró un nombre de categoría duplicado',
					noCategories: 'No se encontraron categorías en el texto ingresado',
					multipleErrors: 'Se encontraron {{count}} errores',
				},
			},
			confirm: {
				alert:
					'Esto buscará y asignará imágenes de portada de unsplash a todos los {{mainEntityLabelPluralLower}} que actualmente no tengan una. El proceso manejará hasta 50 {{mainEntityLabelPluralLower}} en este lote.',
				whatWillHappen: 'Qué sucederá:',
				steps: {
					discover:
						'Descubrir {{mainEntityLabelPluralLower}} sin imágenes de portada',
					extract:
						'Extraer palabras clave de los metadatos {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
					search: 'Buscar imágenes coincidentes en unsplash',
					create: 'Crear artefactos con atribución completa',
					update:
						'Actualizar urls de imágenes de portada {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}}',
				},
			},
			progress: {
				title: 'Enriqueciendo {{mainEntityLabelPluralLower}}...',
				description:
					'Buscando imágenes y actualizando {{mainEntityLabelPluralLower}}',
			},
			results: {
				successful: 'Exitosos',
				failed: 'Fallidos',
				remaining: 'Restantes',
				successfullyEnriched: 'Enriquecidos exitosamente',
				rateLimitWarning: '¡Límite de tasa alcanzado!',
				rateLimitDescription:
					'Se excedió el límite de la API de unsplash. Por favor espera antes de ejecutar otro lote de enriquecimiento.',
			},
			buttons: {
				cancel: 'Cancelar',
				next: 'Siguiente',
				back: 'Atrás',
				startEnrichment: 'Iniciar enriquecimiento',
				processing: 'Procesando...',
				enrichMore: 'Enriquecer más',
				done: 'Listo',
			},
			run: 'Ejecutar',
			success:
				'Enriquecimiento {{mainEntityArticleWithDeLower}} {{mainEntityLabelLower}} iniciado/completado.',
			result: 'Resultado',
			error: 'Error',
		},
		status: {
			title: 'Estado de creación',
			description:
				'Consulta el estado por clave de idempotencia o activa el polling.',
			key: 'Clave de idempotencia',
			fetch: 'Consultar',
			start: 'Iniciar',
			stop: 'Detener',
			result: 'Resultado',
		},
	},
	languages: {
		es: 'Español',
		en: 'Inglés',
	},
	access: {
		free: 'Gratis',
		subscription: 'Suscripción',
		enrollment: 'Inscripción',
		preview: 'Vista previa',
	},
} satisfies AdminLocale

export default admin
